import Alpine from 'alpinejs';
import { App } from './app';
import { Cognito } from './cognito';
import { Api } from './api';
import { Utils } from './utils';
import { actions } from './actions';

const cognito = new Cognito();
const api = new Api();
const app = new App(api);

// Wire the GridJS cell-action handlers (formatters in config.ts call these via Preact onClick).
actions.event = (action, payload) => app.event(action, payload);
actions.get = (action, id) => app.get(action, id);
actions.info = (action, id) => app.info(action, id);

(window as any).Alpine = Alpine;

// Info/error/table dialog store — rendered (escaped) by the modal in index.html.
Alpine.store('dialog', {
    open: false,
    kind: 'info' as 'info' | 'error' | 'table',
    title: '',
    rows: [] as { label: string; value: string }[],
    headers: [] as { key: string; label: string }[],
    tableRows: [] as Record<string, any>[],
    show(title: string, rows: { label: string; value: string }[]) {
        this.kind = 'info'; this.title = title; this.rows = rows; this.open = true;
    },
    showError(title: string, rows: { label: string; value: string }[]) {
        this.kind = 'error'; this.title = title; this.rows = rows; this.open = true;
    },
    showTable(title: string, headers: { key: string; label: string }[], tableRows: Record<string, any>[]) {
        this.kind = 'table'; this.title = title; this.headers = headers; this.tableRows = tableRows; this.open = true;
    },
    close() { this.open = false; }
});

// UI view/visibility state. App pushes to this; the markup binds x-show to it.
Alpine.store('ui', {
    loggedIn: false,
    view: 'grid' as 'grid' | 'client' | 'log',
    activeForm: null as string | null,
    hasEvents: false,
    hasTransactions: false,
});

// Bridge so markup can call App methods via @click (e.g. @click="event('listview')").
Alpine.data('lisa', () => ({
    event: (action: string, payload?: any) => app.event(action, payload),
    send: (action: string) => app.send(action),
    get: (action: string, id: string = '') => app.get(action, id),
    info: (action: string, id: string = '') => app.info(action, id),
    login: (which: string) => { window.location.href = cognito.url(which); },
    authenticate: () => { window.location.href = app.hmrcUrl(); },
    closeForm: (el: HTMLElement) => {
        el.closest('.popup')?.classList.add('hidden');
        (window as any).Alpine.store('ui').activeForm = null;
    },
}));

Alpine.start();

// Handle the OAuth/login redirect back to the app
window.addEventListener('load', () => {
    const handleLoad = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');  // from Cognito OR HMRC
        const state = urlParams.get('state');  // HMRC
        const test = urlParams.get('test'); // useful for testing

        let token = "";

        if (code) {
            if (state) {
                // HMRC OAuth callback — verify the nonce to prevent CSRF
                const storedNonce = localStorage.getItem('hmrc_state');
                if (!storedNonce || storedNonce !== state) {
                    Utils.showInfo("Error", { message: "Invalid state parameter — possible CSRF attempt" });
                    return;
                }
                localStorage.removeItem('hmrc_state');

                token = localStorage.getItem('jwt') ?? '';
                if (!token) {
                    Utils.showInfo("Error", { message: "Session expired — please log in again" });
                    return;
                }
                api.setToken(token);
                await api.authorize(code);
            }
            else {
                token = await cognito.convertToToken(code);
                api.setToken(token);
            }

            (window as any).Alpine.store('ui').loggedIn = true;
            await app.initialize();
        }
        else if (test) {
            api.setToken(test);
            (window as any).Alpine.store('ui').loggedIn = true;
            await app.initialize();
        }
    };

    handleLoad();
});
